import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { XIcon, HashIcon, PhoneIcon, HomeIcon, ClockIcon, CheckCircleIcon, UserIcon, AlertCircleIcon, EditIcon } from 'lucide-react';
interface ActionTimestamp {
  action: string;
  timestamp: string;
  agent: string;
}
interface BuyerLead {
  leadId: string;
  name: string;
  phone: string;
  propertyId?: string;
  entryDate: string;
  entryTime: string;
  callCenterMemo: string;
  callStatus: string;
  callCompleted: boolean;
  qualified: boolean;
  leadSource?: string;
  fullName?: string;
  propertyType?: string;
  rooms?: number;
  budgetMin?: number;
  budgetMax?: number;
  zones?: string[];
  qualificationMemo?: string;
  actionHistory: ActionTimestamp[];
  whoWillMove?: string;
  acquisitionReason?: string;
}
interface LeadDetailsPanelProps {
  lead: BuyerLead;
  onClose: () => void;
  onUpdateStatus?: () => void;
  onLeadUpdate?: (leadId: string, updates: Partial<BuyerLead>) => void;
}
export function LeadDetailsPanel({
  lead,
  onClose,
  onUpdateStatus,
  onLeadUpdate
}: LeadDetailsPanelProps) {
  const getStatusBadge = () => {
    const statusConfig: Record<string, {
      label: string;
      color: string;
    }> = {
      nou: {
        label: 'Nou',
        color: 'bg-blue-100 text-blue-700'
      },
      'de-apelat': {
        label: 'De apelat',
        color: 'bg-purple-100 text-purple-700'
      },
      'nu-raspunde': {
        label: 'Nu răspunde',
        color: 'bg-orange-100 text-orange-700'
      },
      calificare: {
        label: '✨ Calificare',
        color: 'bg-emerald-100 text-emerald-700'
      },
      'vrea-vizionare': {
        label: 'Vrea vizionare',
        color: 'bg-green-100 text-green-700'
      },
      'nu-vrea-vizionare': {
        label: 'Nu vrea vizionare',
        color: 'bg-slate-100 text-slate-700'
      },
      'nu-colaboreaza': {
        label: '🔄 Nu colaborează',
        color: 'bg-orange-100 text-orange-700'
      },
      'comision-redus': {
        label: '💰 Comision redus',
        color: 'bg-yellow-100 text-yellow-700'
      },
      'doreste-vanzare': {
        label: '🏠 Dorește vânzare',
        color: 'bg-blue-100 text-blue-700'
      },
      'a-inchiriat': {
        label: '⛔ A închiriat deja',
        color: 'bg-red-100 text-red-700'
      },
      'numar-invalid': {
        label: '⛔ Număr invalid',
        color: 'bg-red-100 text-red-700'
      }
    };
    const config = statusConfig[lead.callStatus] || {
      label: lead.callStatus,
      color: 'bg-slate-100 text-slate-600'
    };
    return <span className={`px-3 py-1 rounded-full text-xs font-bold ${config.color}`} data-id="element-3589">
        {config.label}
      </span>;
  };
  return <>
      {/* Backdrop with higher z-index */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-100" style={{
      zIndex: 100
    }} data-id="element-3590" />

      {/* Sidebar Panel - starts below navbar */}
      <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      damping: 30,
      stiffness: 300
    }} className="fixed right-0 top-16 bottom-0 w-full sm:w-[650px] bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 shadow-2xl z-110 overflow-y-auto" style={{
      zIndex: 110
    }} data-id="element-3591">
        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-[#132C53] to-[#1e4278] text-white px-6 py-5 shadow-lg" data-id="element-3592">
          <div className="flex items-center justify-between" data-id="element-3593">
            <div data-id="element-3594">
              <div className="flex items-center space-x-2 mb-2" data-id="element-3595">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm" data-id="element-3596">
                  <UserIcon className="w-5 h-5 text-white" data-id="element-3597" />
                </div>
                <div data-id="element-3598">
                  <h3 className="text-xl font-bold" data-id="element-3599">Detalii Lead</h3>
                  <p className="text-sm text-blue-200" data-id="element-3600">Informații Complete</p>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition-colors backdrop-blur-sm" data-id="element-3601">
              <XIcon className="w-6 h-6 text-white" data-id="element-3602" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6" data-id="element-3603">
          {/* Lead Info Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-3604">
            <div className="flex items-start justify-between mb-4" data-id="element-3605">
              <div className="flex items-center space-x-3" data-id="element-3606">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center" data-id="element-3607">
                  <HashIcon className="w-6 h-6 text-white" data-id="element-3608" />
                </div>
                <div data-id="element-3609">
                  <div className="flex items-center space-x-2 mb-1" data-id="element-3610">
                    <span className="text-lg font-bold text-[#132C53]" data-id="element-3611">
                      {lead.leadId}
                    </span>
                    {getStatusBadge()}
                  </div>
                  <h4 className="text-2xl font-bold text-[#132C53]" data-id="element-3612">
                    {lead.name}
                  </h4>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3" data-id="element-3613">
              <div className="flex items-center space-x-2 text-sm" data-id="element-3614">
                <PhoneIcon className="w-4 h-4 text-blue-500" data-id="element-3615" />
                <a href={`tel:${lead.phone}`} className="text-slate-600 hover:text-blue-600 font-medium" data-id="element-3616">
                  {lead.phone}
                </a>
              </div>
              <div className="flex items-center space-x-2 text-sm" data-id="element-3617">
                <ClockIcon className="w-4 h-4 text-purple-500" data-id="element-3618" />
                <span className="text-slate-600" data-id="element-3619">
                  {lead.entryDate} {lead.entryTime}
                </span>
              </div>
              {lead.propertyId && <div className="col-span-2 flex items-center space-x-2 text-sm" data-id="element-3620">
                  <HomeIcon className="w-4 h-4 text-emerald-500" data-id="element-3621" />
                  <span className="text-slate-600" data-id="element-3622">
                    Proprietate:{' '}
                    <span className="font-bold text-emerald-600" data-id="element-3623">
                      {lead.propertyId}
                    </span>
                  </span>
                </div>}
            </div>
          </motion.div>

          {/* Qualification Criteria - Show if lead has structured data */}
          {(lead.propertyType || lead.rooms || lead.budgetMin || lead.zones) && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-3624">
              <h5 className="text-lg font-bold text-[#132C53] mb-5 flex items-center space-x-2" data-id="element-3625">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center" data-id="element-3626">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-600" data-id="element-3627" />
                </div>
                <span data-id="element-3628">Criterii de Calificare</span>
              </h5>

              <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-3629">
                {lead.propertyType && <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-3630">
                    <p className="text-xs text-slate-500 mb-1 font-medium" data-id="element-3631">
                      Tip Imobil
                    </p>
                    <p className="font-bold text-[#132C53]" data-id="element-3632">
                      {lead.propertyType}
                    </p>
                  </div>}

                {lead.rooms && <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-3633">
                    <p className="text-xs text-slate-500 mb-1 font-medium" data-id="element-3634">
                      Nr. Camere
                    </p>
                    <p className="font-bold text-[#132C53]" data-id="element-3635">{lead.rooms}</p>
                  </div>}

                {(lead.budgetMin || lead.budgetMax) && <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-200" data-id="element-3636">
                    <p className="text-xs text-emerald-600 mb-1 font-medium" data-id="element-3637">
                      Buget
                    </p>
                    <p className="font-bold text-emerald-700" data-id="element-3638">
                      {lead.budgetMin && lead.budgetMax ? `${lead.budgetMin}-${lead.budgetMax} EUR` : lead.budgetMin ? `${lead.budgetMin} EUR` : `${lead.budgetMax} EUR`}
                    </p>
                  </div>}

                {lead.zones && lead.zones.length > 0 && <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-3639">
                    <p className="text-xs text-slate-500 mb-1 font-medium" data-id="element-3640">
                      Zone
                    </p>
                    <p className="font-bold text-[#132C53]" data-id="element-3641">
                      {lead.zones.length}
                    </p>
                  </div>}
              </div>

              {lead.zones && lead.zones.length > 0 && <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 mb-3 border border-slate-200" data-id="element-3642">
                  <p className="text-xs text-slate-500 mb-2 font-medium" data-id="element-3643">
                    Zone Preferate
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-3644">
                    {lead.zones.map((zone, idx) => <span key={idx} className="px-3 py-1 bg-white border border-emerald-200 text-emerald-700 rounded-full text-xs font-semibold shadow-sm" data-id="element-3645">
                        {zone}
                      </span>)}
                  </div>
                </div>}

              {lead.qualificationMemo && <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-3646">
                  <p className="text-xs text-slate-500 mb-1 font-medium" data-id="element-3647">
                    Memo Calificare
                  </p>
                  <p className="text-sm text-slate-700" data-id="element-3648">
                    {lead.qualificationMemo}
                  </p>
                </div>}

              {(lead.whoWillMove || lead.acquisitionReason) && <div className="border-t border-slate-200 pt-4 mt-4" data-id="element-3649">
                  <p className="text-sm font-semibold text-slate-700 mb-3" data-id="element-3650">
                    Întrebări Calificare
                  </p>
                  <div className="space-y-3" data-id="element-3651">
                    {lead.whoWillMove && <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-3652">
                        <p className="text-xs text-slate-500 mb-1 font-medium" data-id="element-3653">
                          Cine se va muta în noul imobil?
                        </p>
                        <p className="text-sm text-slate-700" data-id="element-3654">
                          {lead.whoWillMove}
                        </p>
                      </div>}
                    {lead.acquisitionReason && <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-3655">
                        <p className="text-xs text-slate-500 mb-1 font-medium" data-id="element-3656">
                          De ce se dorește o altă achiziție de imobil?
                        </p>
                        <p className="text-sm text-slate-700" data-id="element-3657">
                          {lead.acquisitionReason}
                        </p>
                      </div>}
                  </div>
                </div>}
            </motion.div>}

          {/* Status Info */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-3658">
            <div className="flex items-center justify-between mb-4" data-id="element-3659">
              <h5 className="text-lg font-bold text-[#132C53] flex items-center space-x-2" data-id="element-3660">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-3661">
                  <AlertCircleIcon className="w-5 h-5 text-blue-600" data-id="element-3662" />
                </div>
                <span data-id="element-3663">Status Procesare</span>
              </h5>
              {onUpdateStatus && !['a-inchiriat', 'numar-invalid'].includes(lead.callStatus) && <button onClick={onUpdateStatus} className="flex items-center space-x-1 px-3 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl text-sm font-bold hover:shadow-lg transition-all" data-id="element-3664">
                    <EditIcon className="w-4 h-4" data-id="element-3665" />
                    <span data-id="element-3666">Modifică</span>
                  </button>}
            </div>

            <div className="space-y-3" data-id="element-3667">
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-3668">
                <p className="text-xs text-slate-500 mb-2 font-medium" data-id="element-3669">
                  Status Curent
                </p>
                {getStatusBadge()}
              </div>

              <div className="grid grid-cols-2 gap-3" data-id="element-3670">
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-3671">
                  <p className="text-xs text-slate-500 mb-1 font-medium" data-id="element-3672">
                    Status Apel
                  </p>
                  <p className="font-bold text-[#132C53]" data-id="element-3673">
                    {lead.callCompleted ? 'Apelat' : 'Neapelat'}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-3674">
                  <p className="text-xs text-slate-500 mb-1 font-medium" data-id="element-3675">
                    Calificare
                  </p>
                  <p className="font-bold text-[#132C53]" data-id="element-3676">
                    {lead.qualified ? 'Calificat' : 'Necalificat'}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Lead Source */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-3677">
            <h5 className="text-lg font-bold text-[#132C53] mb-4 flex items-center space-x-2" data-id="element-3678">
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center" data-id="element-3679">
                <HomeIcon className="w-5 h-5 text-emerald-600" data-id="element-3680" />
              </div>
              <span data-id="element-3681">Surse Lead</span>
            </h5>

            <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-200" data-id="element-3682">
              <p className="text-xs text-emerald-600 mb-2 font-medium" data-id="element-3683">Sursă</p>
              {lead.propertyId ? <div className="flex items-center space-x-2" data-id="element-3684">
                  <HomeIcon className="w-5 h-5 text-emerald-600" data-id="element-3685" />
                  <span className="font-bold text-emerald-700 text-lg" data-id="element-3686">
                    {lead.propertyId}
                  </span>
                </div> : <div className="flex items-center space-x-2" data-id="element-3687">
                  <AlertCircleIcon className="w-5 h-5 text-slate-600" data-id="element-3688" />
                  <span className="font-bold text-slate-700 text-lg" data-id="element-3689">
                    {lead.leadSource || 'generat-suport'}
                  </span>
                </div>}
            </div>
          </motion.div>

          {/* Call Center Memo - only show if there's actual memo content */}
          {lead.callCenterMemo && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6
        }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-3690">
              <h5 className="text-lg font-semibold text-[#132C53] mb-3 flex items-center space-x-2" data-id="element-3691">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center" data-id="element-3692">
                  <ClockIcon className="w-5 h-5 text-purple-600" data-id="element-3693" />
                </div>
                <span data-id="element-3694">Memo Call Center</span>
              </h5>
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-3695">
                <p className="text-sm text-slate-700" data-id="element-3696">{lead.callCenterMemo}</p>
              </div>
            </motion.div>}

          {/* Action History */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.7
        }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-3697">
            <h5 className="text-lg font-semibold text-[#132C53] mb-4 flex items-center space-x-2" data-id="element-3698">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-3699">
                <CheckCircleIcon className="w-5 h-5 text-blue-600" data-id="element-3700" />
              </div>
              <span data-id="element-3701">Istoric Acțiuni</span>
            </h5>
            <div className="space-y-3" data-id="element-3702">
              {lead.actionHistory.map((action, idx) => <div key={idx} className="flex items-start space-x-3 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-3703">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md" data-id="element-3704">
                    <CheckCircleIcon className="w-5 h-5 text-white" data-id="element-3705" />
                  </div>
                  <div className="flex-1" data-id="element-3706">
                    <p className="text-sm font-semibold text-[#132C53] mb-1" data-id="element-3707">
                      {action.action}
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-3708">
                      {action.timestamp} • {action.agent}
                    </p>
                  </div>
                </div>)}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </>;
}